import { lazy, Suspense } from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { useAuth, AuthProvider } from './hooks/useAuth.jsx'
import Layout from './components/Layout'
import LoadingSpinner from './components/LoadingSpinner'
import RequirePermission from './components/RequirePermission'
import { useMobileWebProfile } from './hooks/useMobileWebProfile'

function RedirectResetPassword() {
  const { search } = useLocation()
  return <Navigate to={`/reset-password${search}`} replace />
}

const Home = lazy(() => import('./pages/Home'))
const Login = lazy(() => import('./pages/Login'))
const ChangePassword = lazy(() => import('./pages/ChangePassword'))
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'))
const ResetPassword = lazy(() => import('./pages/ResetPassword'))
const Dashboard = lazy(() => import('./pages/Dashboard'))
const HomeDashboard = lazy(() => import('./pages/HomeDashboard'))
const Stock = lazy(() => import('./pages/Stock'))
const Suppliers = lazy(() => import('./pages/Suppliers'))
const Movements = lazy(() => import('./pages/Movements'))
const Alerts = lazy(() => import('./pages/Alerts'))
const UserManagement = lazy(() => import('./pages/UserManagement'))
const UserDetail = lazy(() => import('./pages/UserDetail'))
const PainterDashboard = lazy(() => import('./pages/PainterDashboard'))
const PainterStockSimple = lazy(() => import('./pages/PainterStockSimple'))
const PainterRecordUsage = lazy(() => import('./pages/PainterRecordUsage'))
const PainterMovements = lazy(() => import('./pages/PainterMovements'))
const PainterAlerts = lazy(() => import('./pages/PainterAlerts'))
const PainterTransfer = lazy(() => import('./pages/PainterTransfer'))
const PainterRequest = lazy(() => import('./pages/PainterRequest'))
const PainterMyRequests = lazy(() => import('./pages/PainterMyRequests'))
const PainterMonthlyRestock = lazy(() => import('./pages/PainterMonthlyRestock'))
const PainterMonthlyRestockForm = lazy(() => import('./pages/PainterMonthlyRestockForm'))
const MonthlyRestocks = lazy(() => import('./pages/MonthlyRestocks'))
const AdminMaterialRequests = lazy(() => import('./pages/AdminMaterialRequests'))
const PurchaseOrders = lazy(() => import('./pages/PurchaseOrders'))
const PainterPurchaseOrders = lazy(() => import('./pages/PainterPurchaseOrders'))
const Clients = lazy(() => import('./pages/jobs/Clients'))
const ClientDetail = lazy(() => import('./pages/jobs/ClientDetail'))
const Quotations = lazy(() => import('./pages/jobs/Quotations'))
const Jobs = lazy(() => import('./pages/jobs/Jobs'))
const JobDetail = lazy(() => import('./pages/jobs/JobDetail'))
const JobsDashboard = lazy(() => import('./pages/jobs/JobsDashboard'))
const JobsStockUsage = lazy(() => import('./pages/jobs/JobsStockUsage'))
const JobsTimeTracking = lazy(() => import('./pages/jobs/JobsTimeTracking'))
const WorkerMyJobs = lazy(() => import('./pages/worker/WorkerMyJobs'))
const WorkerJobDetail = lazy(() => import('./pages/worker/WorkerJobDetail'))
const WorkerDefectSheet = lazy(() => import('./pages/worker/WorkerDefectSheet'))
const WorkerHours = lazy(() => import('./pages/worker/WorkerHours'))
const JobDefectSheet = lazy(() => import('./pages/jobs/JobDefectSheet'))
const DefectSheetJobs = lazy(() => import('./pages/jobs/DefectSheetJobs'))
const AdminDefectSheets = lazy(() => import('./pages/jobs/AdminDefectSheets'))
const DefectSheetById = lazy(() => import('./pages/jobs/DefectSheetById'))
const DefectSheetOverviewById = lazy(() => import('./pages/jobs/DefectSheetOverviewById'))
const WorkerDefectSheetView = lazy(() => import('./pages/worker/WorkerDefectSheetView'))
const MobileProfileDashboard = lazy(() => import('./pages/mobile/MobileProfileDashboard'))

const routeFallback = (
  <div className="flex min-h-[50vh] items-center justify-center py-12">
    <LoadingSpinner size="lg" />
  </div>
)

function AppContent() {
  const { user, loading } = useAuth()
  const { appliesMobileProfile, defaultRoute: mobileDefaultRoute } = useMobileWebProfile()

  if (loading) {
    return <LoadingSpinner />
  }

  if (!user) {
    return (
      <Suspense fallback={routeFallback}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/login" element={<Login key="login" />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/redefinir-senha" element={<RedirectResetPassword />} />
          <Route path="/change-password" element={<ChangePassword />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    )
  }

  if (user.is_temporary_password) {
    return (
      <Suspense fallback={routeFallback}>
        <ChangePassword />
      </Suspense>
    )
  }

  const defaultRoute =
    user?.role_id === 2
      ? '/painter/dashboard'
      : appliesMobileProfile
        ? mobileDefaultRoute
        : '/dashboard'

  return (
    <Layout>
      <Suspense fallback={routeFallback}>
        <Routes>
          <Route path="/" element={<Navigate to={defaultRoute} replace />} />

          <Route path="/mobile/dashboard" element={<MobileProfileDashboard />} />
          <Route path="/dashboard" element={<HomeDashboard />} />
          <Route path="/stock" element={<Stock />} />
          <Route path="/stock/dashboard" element={<Dashboard />} />
          <Route path="/stock/:locationId" element={<Stock />} />
          <Route path="/suppliers" element={<Suppliers />} />
          <Route path="/movements" element={<Movements />} />
          <Route path="/alerts" element={<Alerts />} />
          <Route path="/users" element={<UserManagement />} />
          <Route path="/users/:id" element={<UserDetail />} />
          <Route path="/material-requests" element={<AdminMaterialRequests />} />
          <Route path="/monthly-restocks" element={<MonthlyRestocks />} />
          <Route path="/purchase-orders" element={<PurchaseOrders />} />

          <Route path="/jobs/dashboard" element={<JobsDashboard />} />
          <Route path="/jobs/clients/:id" element={<ClientDetail />} />
          <Route path="/jobs/clients" element={<Clients />} />
          <Route
            path="/jobs/quotations"
            element={
              <RequirePermission permission="quotations_view">
                <Quotations />
              </RequirePermission>
            }
          />
          <Route path="/jobs/stock-usage" element={<JobsStockUsage />} />
          <Route path="/jobs/time-tracking" element={<JobsTimeTracking />} />
          <Route
            path="/jobs/defect-sheets"
            element={
              <RequirePermission permissions={['defect_sheets', 'defect_sheets_view']}>
                <AdminDefectSheets />
              </RequirePermission>
            }
          />
          <Route
            path="/jobs/defect-sheets/:sheetId/view"
            element={
              <RequirePermission permissions={['defect_sheets', 'defect_sheets_view']}>
                <DefectSheetOverviewById />
              </RequirePermission>
            }
          />
          <Route
            path="/jobs/defect-sheets/:sheetId"
            element={
              <RequirePermission permissions={['defect_sheets', 'defect_sheets_view']}>
                <DefectSheetById />
              </RequirePermission>
            }
          />
          <Route path="/jobs/:id/defect-sheet" element={<JobDefectSheet />} />
          <Route path="/jobs/:id" element={<JobDetail />} />
          <Route path="/jobs" element={<Jobs />} />

          <Route
            path="/worker/jobs"
            element={
              <RequirePermission permission="field_jobs">
                <WorkerMyJobs />
              </RequirePermission>
            }
          />
          <Route
            path="/worker/defect-sheets"
            element={
              <RequirePermission permissions={['defect_sheets', 'defect_sheets_view']}>
                <DefectSheetJobs />
              </RequirePermission>
            }
          />
          <Route
            path="/worker/defect-sheets/:sheetId/view"
            element={
              <RequirePermission permissions={['defect_sheets', 'defect_sheets_view']}>
                <DefectSheetOverviewById />
              </RequirePermission>
            }
          />
          <Route
            path="/worker/defect-sheets/:sheetId"
            element={
              <RequirePermission permissions={['defect_sheets', 'defect_sheets_view']}>
                <DefectSheetById />
              </RequirePermission>
            }
          />
          <Route
            path="/worker/jobs/:id/defect-sheet/view"
            element={
              <RequirePermission permissions={['defect_sheets', 'defect_sheets_view']}>
                <WorkerDefectSheetView />
              </RequirePermission>
            }
          />
          <Route
            path="/worker/jobs/:id/defect-sheet"
            element={
              <RequirePermission permission="defect_sheets">
                <WorkerDefectSheet />
              </RequirePermission>
            }
          />
          <Route
            path="/worker/jobs/:id"
            element={
              <RequirePermission permission="field_jobs">
                <WorkerJobDetail />
              </RequirePermission>
            }
          />
          <Route
            path="/worker/hours"
            element={
              <RequirePermission permission="field_hours">
                <WorkerHours />
              </RequirePermission>
            }
          />

          <Route path="/painter/dashboard" element={<PainterDashboard />} />
          <Route path="/painter/stock" element={<PainterStockSimple />} />
          <Route path="/painter/record-usage" element={<PainterRecordUsage />} />
          <Route path="/painter/search" element={<PainterStockSimple />} />
          <Route path="/painter/transfer" element={<PainterTransfer />} />
          <Route path="/painter/request" element={<PainterRequest />} />
          <Route path="/painter/my-requests" element={<PainterMyRequests />} />
          <Route path="/painter/monthly-restock" element={<PainterMonthlyRestock />} />
          <Route path="/painter/monthly-restock/new" element={<PainterMonthlyRestockForm />} />
          <Route path="/painter/purchase-orders" element={<PainterPurchaseOrders />} />
          <Route path="/painter/movements" element={<PainterMovements />} />
          <Route path="/painter/alerts" element={<PainterAlerts />} />

          <Route path="*" element={<Navigate to={defaultRoute} replace />} />
        </Routes>
      </Suspense>
    </Layout>
  )
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  )
}

export default App
